import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ChevronLeft,
  Paperclip,
  Sparkles,
  MessageSquare,
  Building2,
  Wrench,
  Pencil,
  Loader2,
  Download,
  FileDown,
  AlertTriangle,
  Link2,
  Receipt,
  BookPlus,
  BookOpen,
  Camera,
  CheckCheck,
  Cpu,
  Code2,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { QuoteDialog, QuotesList } from "@/components/QuoteDialog";
import { VoiceDictationButton } from "@/components/VoiceDictationButton";
import { CloseWithReportDialog } from "@/components/CloseWithReportDialog";
import { CarnetPanel } from "@/components/CarnetPanel";
import { SendPaymentLinkDialog } from "@/components/SendPaymentLinkDialog";
import { KnowledgeFilesPanel } from "@/components/KnowledgeFilesPanel";
import { LicensePicker } from "@/components/LicensePicker";
import { WhatsAppOpenChatButton } from "@/components/WhatsAppOpenChatButton";
import { EmailComposeButton } from "@/components/EmailComposeButton";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import { addTicketNote } from "@/lib/offlineQueue";

const priorityColor = {
  critica: "bg-red-100 text-red-800 border-red-200",
  alta: "bg-amber-100 text-amber-800 border-amber-200",
  media: "bg-zinc-100 text-zinc-800 border-zinc-200",
  bassa: "bg-zinc-50 text-zinc-600 border-zinc-200",
};
const statusColor = {
  aperto: "bg-red-50 text-red-700 border-red-200",
  in_lavorazione: "bg-amber-50 text-amber-800 border-amber-200",
  risolto: "bg-emerald-50 text-emerald-800 border-emerald-200",
  chiuso: "bg-zinc-100 text-zinc-700 border-zinc-200",
};

function EventItem({ ev }) {
  const iconMap = {
    created: Wrench,
    status_change: Wrench,
    assignment: Wrench,
    attachment: Paperclip,
    note: MessageSquare,
    email_out: Mail,
    intervention_type_change: Wrench,
  };
  const Icon = iconMap[ev.type] || MessageSquare;
  return (
    <div className="flex gap-3 py-3">
      <div className="h-7 w-7 shrink-0 rounded-sm bg-zinc-100 flex items-center justify-center text-zinc-600">
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-800 whitespace-pre-wrap">{ev.content}</div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
          {ev.user_name} · {new Date(ev.created_at).toLocaleString("it-IT")}
        </div>
      </div>
    </div>
  );
}

export default function TicketDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ticket, setTicket] = useState(null);
  const [events, setEvents] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [users, setUsers] = useState([]);
  const [note, setNote] = useState("");
  const [timelineFilter, setTimelineFilter] = useState("all"); // all | status | attachments | whatsapp
  const [timelineSearch, setTimelineSearch] = useState("");
  const [ai, setAi] = useState({ loading: false, suggestion: "" });
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileSerial, setReconcileSerial] = useState("");
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [dispensers, setDispensers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [customerPricing, setCustomerPricing] = useState(null);
  const fileRef = useRef();
  const cameraRef = useRef();

  const load = useCallback(async () => {
    try {
      const [t, e, a, u, d, q] = await Promise.all([
        api.get(`/tickets/${id}`),
        api.get(`/tickets/${id}/events`),
        api.get(`/tickets/${id}/attachments`),
        api.get(`/users`),
        api.get(`/dispensers`),
        api.get(`/quotes`, { params: { ticket_id: id } }),
      ]);
      setTicket(t.data); setEvents(e.data); setAttachments(a.data); setUsers(u.data); setDispensers(d.data); setQuotes(q.data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setCustomerPricing({
        hourly_rate: data.end_customer_hourly_rate,
        carnet_10h_price: data.end_customer_carnet_10h_price,
      });
    }).catch(() => { /* ignore, non blocante */ });
  }, []);

  // Poll payment status after returning from Stripe checkout (`?payment_session=cs_test_xxx`)
  useEffect(() => {
    const sid = searchParams.get("payment_session");
    if (!sid) return;
    const cancelled = searchParams.get("payment_cancelled") === "1";
    if (cancelled) { toast.error(t("ticket_detail.toast_payment_cancelled")); return; }
    let attempts = 0;
    const maxAttempts = 8;
    let cancel = false;
    const poll = async () => {
      if (cancel || attempts >= maxAttempts) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success(`Pagamento ricevuto · €${(data.amount || 0).toFixed(2)}`, { duration: 6000 });
          const next = new URLSearchParams(searchParams);
          next.delete("payment_session");
          setSearchParams(next, { replace: true });
          await load();
          return;
        }
        if (data.status === "expired") {
          toast.error(t("ticket_detail.toast_payment_expired"));
          return;
        }
      } catch (err) {
        // Transient error during Stripe status polling — will retry with backoff below
        console.debug("payment status polling attempt failed, will retry:", err?.message);
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancel = true; };
  }, [searchParams, setSearchParams, load]);

  const updateField = async (patch) => {
    try {
      await api.put(`/tickets/${id}`, patch);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      const res = await addTicketNote(id, note);
      if (res.queued) toast.success(t("ticket_detail.toast_note_offline"));
      setNote("");
      if (!res.queued) await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/tickets/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("ticket_detail.toast_attachment_uploaded"));
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runAi = async () => {
    setAi({ loading: true, suggestion: "" });
    try {
      const { data } = await api.post("/ai/suggest", { ticket_id: id });
      setAi({ loading: false, suggestion: data.suggestion });
    } catch (e) {
      setAi({ loading: false, suggestion: "" });
      toast.error(formatApiError(e));
    }
  };

  const openReconcile = () => {
    setReconcileSerial("");
    setReconcileOpen(true);
  };

  const submitReconcile = async () => {
    const s = (reconcileSerial || "").trim();
    if (!s) { toast.error(t("ticket_detail.toast_reconcile_serial_required")); return; }
    setReconcileLoading(true);
    try {
      await api.post(`/tickets/${id}/reconcile`, { dispenser_serial: s });
      toast.success(t("ticket_detail.toast_reconcile_success"));
      setReconcileOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReconcileLoading(false);
    }
  };

  const addToKnowledge = async () => {
    try {
      await api.post(`/knowledge/from-ticket/${id}`);
      toast.success(t("ticket_detail.toast_added_to_kb"));
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!ticket) return <div className="p-8 text-sm text-zinc-500">Caricamento...</div>;

  // httpOnly cookie is sent automatically with <img> requests (same-origin)
  const dl = (fid) => `${API_BASE}/attachments/${fid}/download`;

  return (
    <div className="p-4 sm:p-8 max-w-7xl">
      <Link to="/tickets" data-testid="back-to-tickets" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-500 hover:text-brand">
        <ChevronLeft className="h-3.5 w-3.5" /> Ticket
      </Link>

      <div className="mt-3 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 lg:gap-6 mb-6 sm:mb-8">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-mono">
            {ticket.ticket_number}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-zinc-950 tracking-tighter break-words">
            {ticket.title}
          </h1>
          <div className="flex items-center gap-2 mt-3">
            <Badge className={`rounded-sm text-[10px] uppercase tracking-widest border ${priorityColor[ticket.priority]}`}>{ticket.priority}</Badge>
            <Badge className={`rounded-sm text-[10px] uppercase tracking-widest border ${statusColor[ticket.status]}`}>{ticket.status.replace("_", " ")}</Badge>
            <Badge
              data-testid="ticket-intervention-badge"
              className={`rounded-sm text-[10px] uppercase tracking-widest border font-sans ${
                (ticket.intervention_type || "hardware") === "software"
                  ? "bg-violet-100 text-violet-800 border-violet-200"
                  : "bg-sky-50 text-sky-800 border-sky-200"
              }`}
            >
              {(ticket.intervention_type || "hardware") === "software"
                ? <><Code2 className="h-2.5 w-2.5 mr-1 inline" /> {t("tickets.intervention_software")}</>
                : <><Cpu className="h-2.5 w-2.5 mr-1 inline" /> {t("tickets.intervention_hardware")}</>}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full lg:w-auto">
          {(ticket.status !== "risolto" && ticket.status !== "chiuso") && (
            <Button
              size="sm"
              data-testid="close-with-report-btn"
              onClick={() => setCloseOpen(true)}
              className="h-9 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <CheckCheck className="h-4 w-4 mr-2" /> Chiudi con report
            </Button>
          )}
          {(ticket.status === "risolto" || ticket.status === "chiuso") && (
            <Button
              size="sm"
              variant="outline"
              data-testid="add-to-kb-btn"
              onClick={addToKnowledge}
              className="h-9 rounded-sm border-brand/30 text-brand hover:bg-brand-muted"
            >
              <BookPlus className="h-4 w-4 mr-2" /> Aggiungi a Conoscenza AI
            </Button>
          )}
          <a
            href={`${API_BASE}/tickets/${id}/pdf`}
            target="_blank"
            rel="noreferrer"
            data-testid="ticket-pdf-btn"
            className="inline-flex items-center gap-2 h-9 px-3 rounded-sm border border-zinc-300 text-sm text-zinc-800 hover:bg-zinc-50"
          >
            <FileDown className="h-4 w-4" /> PDF
          </a>
          <Select value={ticket.status} onValueChange={(v) => updateField({ status: v })}>
            <SelectTrigger data-testid="ticket-status-select" className="flex-1 min-w-[140px] sm:flex-none sm:w-44 rounded-sm h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="aperto">Aperto</SelectItem>
              <SelectItem value="in_lavorazione">In lavorazione</SelectItem>
              <SelectItem value="risolto">Risolto</SelectItem>
              <SelectItem value="chiuso">Chiuso</SelectItem>
              <SelectItem value="in_attesa_pagamento">In attesa pagamento</SelectItem>
            </SelectContent>
          </Select>
          <Select value={ticket.assigned_to || "none"} onValueChange={(v) => updateField({ assigned_to: v === "none" ? null : v })}>
            <SelectTrigger data-testid="ticket-assignee-select" className="flex-1 min-w-[160px] sm:flex-none sm:w-52 rounded-sm h-9"><SelectValue placeholder={t("ticket_detail.assign_placeholder")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Nessuno —</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={ticket.intervention_type || "hardware"} onValueChange={(v) => updateField({ intervention_type: v })}>
            <SelectTrigger data-testid="ticket-intervention-select" className="flex-1 min-w-[140px] sm:flex-none sm:w-40 rounded-sm h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="hardware"><span className="inline-flex items-center gap-2"><Cpu className="h-3.5 w-3.5" /> {t("tickets.intervention_hardware")}</span></SelectItem>
              <SelectItem value="software"><span className="inline-flex items-center gap-2"><Code2 className="h-3.5 w-3.5" /> {t("tickets.intervention_software")}</span></SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* MAIN COL */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="rounded-sm border-zinc-200 bg-white p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Descrizione</div>
            <div className="text-sm text-zinc-800 whitespace-pre-wrap">{ticket.description}</div>
          </Card>

          <Card className="rounded-sm border-zinc-200 bg-white p-6">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <h2 className="font-display text-lg font-semibold text-zinc-900 tracking-tight">Timeline intervento</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden text-[10px] uppercase tracking-widest">
                  {[
                    { key: "all", label: "Tutti" },
                    { key: "status", label: "Solo stato" },
                    { key: "attachments", label: "Allegati" },
                    { key: "whatsapp", label: "WhatsApp" },
                  ].map((opt, i) => (
                    <button
                      key={opt.key}
                      data-testid={`timeline-filter-${opt.key}`}
                      type="button"
                      onClick={() => setTimelineFilter(opt.key)}
                      className={`px-3 py-1.5 transition-colors ${i > 0 ? "border-l border-zinc-200" : ""} ${
                        timelineFilter === opt.key
                          ? "bg-brand text-white"
                          : "bg-white text-zinc-600 hover:bg-zinc-50"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mb-3 relative">
              <Input
                data-testid="timeline-search"
                value={timelineSearch}
                onChange={(e) => setTimelineSearch(e.target.value)}
                placeholder="Cerca nel timeline (testo, nome utente, allegato...)"
                className="pl-3 pr-10 h-9 rounded-sm text-sm"
              />
              {timelineSearch && (
                <button
                  type="button"
                  data-testid="timeline-search-clear"
                  onClick={() => setTimelineSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 text-lg leading-none"
                  aria-label="Pulisci ricerca"
                >
                  ×
                </button>
              )}
            </div>
            {(() => {
              let list = events;
              if (timelineFilter === "status") list = list.filter((e) => e.type === "status_change" || e.type === "created");
              else if (timelineFilter === "attachments") list = list.filter((e) => e.type === "attachment");
              else if (timelineFilter === "whatsapp") list = list.filter((e) => (e.content || "").toLowerCase().includes("whatsapp"));
              const q = timelineSearch.trim().toLowerCase();
              if (q) list = list.filter((e) => `${e.content || ""} ${e.user_name || ""}`.toLowerCase().includes(q));
              return (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2" data-testid="timeline-count">
                    {list.length} eventi{q ? ` (filtrati)` : ""}
                  </div>
                  <div className="divide-y divide-zinc-100">
                    {list.length === 0 ? (
                      <div className="py-6 text-center text-sm text-zinc-500" data-testid="timeline-empty">
                        {q ? `Nessun evento contiene "${timelineSearch}"` : "Nessun evento con i filtri attivi"}
                      </div>
                    ) : (
                      list.map((e) => <EventItem key={e.id} ev={e} />)
                    )}
                  </div>
                </>
              );
            })()}
            <div className="mt-4 pt-4 border-t border-zinc-200">
              <Textarea
                data-testid="note-input"
                rows={3}
                placeholder={t("ticket_detail.add_note_placeholder")}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="rounded-sm"
              />
              <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    ref={fileRef}
                    type="file"
                    className="hidden"
                    accept="image/*,application/pdf"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadFile(f);
                      e.target.value = "";
                    }}
                    data-testid="file-input"
                  />
                  <input
                    ref={cameraRef}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadFile(f);
                      e.target.value = "";
                    }}
                    data-testid="camera-input"
                  />
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => cameraRef.current?.click()} data-testid="camera-btn">
                    <Camera className="h-3.5 w-3.5 mr-1.5" /> Scatta foto
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => fileRef.current?.click()} data-testid="upload-btn">
                    <Paperclip className="h-3.5 w-3.5 mr-1.5" /> Allega file
                  </Button>
                  <VoiceDictationButton value={note} onChange={setNote} />
                </div>
                <Button
                  data-testid="add-note-btn"
                  size="sm"
                  className="bg-brand hover:bg-brand-dark text-white rounded-sm"
                  onClick={addNote}
                >
                  Aggiungi nota
                </Button>
              </div>
            </div>
          </Card>

          {attachments.length > 0 && (
            <Card className="rounded-sm border-zinc-200 bg-white p-6">
              <h2 className="font-display text-lg font-semibold text-zinc-900 tracking-tight mb-4">Allegati</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {attachments.map((a) => {
                  const ct = (a.content_type || "").toLowerCase();
                  const isImg = ct.startsWith("image/");
                  const isVideo = ct.startsWith("video/");
                  const isAudio = ct.startsWith("audio/");

                  if (isVideo) {
                    return (
                      <div
                        key={a.id}
                        className="col-span-2 md:col-span-3 border border-zinc-200 rounded-sm overflow-hidden bg-black"
                        data-testid={`attachment-${a.id}`}
                      >
                        <video
                          data-testid={`attachment-video-${a.id}`}
                          src={dl(a.id)}
                          controls
                          preload="metadata"
                          playsInline
                          className="w-full max-h-[420px] bg-black"
                        >
                          <track kind="captions" />
                          Il tuo browser non supporta il player video.
                        </video>
                        <div className="p-2 flex items-center justify-between gap-2 bg-white text-xs">
                          <span className="text-zinc-700 truncate flex-1">{a.original_filename}</span>
                          <a
                            href={dl(a.id)}
                            download
                            className="inline-flex items-center gap-1 text-brand hover:underline shrink-0"
                            data-testid={`attachment-download-${a.id}`}
                          >
                            <Download className="h-3 w-3" /> Scarica
                          </a>
                        </div>
                      </div>
                    );
                  }

                  if (isAudio) {
                    return (
                      <div
                        key={a.id}
                        className="col-span-2 md:col-span-3 border border-zinc-200 rounded-sm bg-zinc-50 p-3"
                        data-testid={`attachment-${a.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-sm bg-brand-muted text-brand flex items-center justify-center shrink-0">
                            <Paperclip className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-zinc-700 truncate">{a.original_filename}</div>
                            <audio
                              data-testid={`attachment-audio-${a.id}`}
                              src={dl(a.id)}
                              controls
                              preload="metadata"
                              className="mt-1 w-full h-8"
                            >
                              Il tuo browser non supporta il player audio.
                            </audio>
                          </div>
                          <a
                            href={dl(a.id)}
                            download
                            className="inline-flex items-center gap-1 text-xs text-brand hover:underline shrink-0"
                            data-testid={`attachment-download-${a.id}`}
                          >
                            <Download className="h-3 w-3" /> Scarica
                          </a>
                        </div>
                      </div>
                    );
                  }

                  // Image or generic file — same as before
                  return (
                    <a
                      key={a.id}
                      href={dl(a.id)}
                      target="_blank"
                      rel="noreferrer"
                      className="block border border-zinc-200 rounded-sm overflow-hidden hover:border-brand"
                      data-testid={`attachment-${a.id}`}
                    >
                      {isImg ? (
                        <img src={dl(a.id)} alt={a.original_filename} className="w-full h-32 object-cover" />
                      ) : (
                        <div className="h-32 flex items-center justify-center bg-zinc-50 text-zinc-500">
                          <Download className="h-6 w-6" />
                        </div>
                      )}
                      <div className="p-2 text-xs text-zinc-700 truncate">{a.original_filename}</div>
                    </a>
                  );
                })}
              </div>
            </Card>
          )}
        </div>

        {/* SIDE COL */}
        <div className="space-y-6">
          <Card className="rounded-sm border-zinc-200 bg-white p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Erogatore</div>
            {ticket.is_orphan && (
              <div data-testid="orphan-banner" className="mb-4 rounded-sm border border-orange-200 bg-orange-50 p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-700 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-orange-900 uppercase tracking-wider">Da riconciliare</div>
                    <div className="text-xs text-orange-800 mt-1">
                      Seriale <span className="font-mono">{ticket.dispenser_serial}</span> non presente in anagrafica.
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="reconcile-open-btn"
                      onClick={openReconcile}
                      className="mt-2 h-8 rounded-sm border-orange-300 bg-white text-orange-900 hover:bg-orange-100"
                    >
                      <Link2 className="h-3.5 w-3.5 mr-2" /> Associa a dispenser esistente
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-start gap-3">
              <BrandLogo size="xl" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-mono text-sm text-zinc-900 flex-1">{ticket.dispenser_serial}</div>
                  {user?.role !== "tecnico_distributore" && (
                    <button
                      onClick={() => { setReconcileSerial(ticket.dispenser_serial && ticket.dispenser_serial !== "N/D" ? ticket.dispenser_serial : ""); setReconcileOpen(true); }}
                      className="h-6 w-6 rounded-sm border border-zinc-300 text-zinc-500 hover:text-brand hover:border-brand flex items-center justify-center shrink-0"
                      title={t("ticket_detail.edit_serial")}
                      data-testid="edit-serial-btn"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <div className="text-sm font-medium text-zinc-900">{ticket.dispenser?.model || "—"}</div>
                {ticket.dispenser?.location && (
                  <div className="text-xs text-zinc-500 mt-0.5">{ticket.dispenser.location}</div>
                )}
              </div>
            </div>
            {ticket.warranty && (
              <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="warranty-badge">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Garanzia</div>
                <div className={`inline-flex items-center gap-2 text-xs px-2 py-1 rounded-sm border ${ticket.warranty.in_warranty ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-800 border-red-200"}`}>
                  <span className="font-semibold">{ticket.warranty.in_warranty ? t("ticket_detail.warranty_in") : t("ticket_detail.warranty_out")}</span>
                  <span className="text-zinc-500">·</span>
                  <span>scad. {new Date(ticket.warranty.expiry_date).toLocaleDateString("it-IT")}</span>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                  Base: {ticket.warranty.source} · {new Date(ticket.warranty.base_date).toLocaleDateString("it-IT")}
                  {ticket.warranty.in_warranty && ` · ${ticket.warranty.days_left} gg residui`}
                </div>
              </div>
            )}
            {ticket.customer_name && (
              <div className="mt-4 pt-4 border-t border-zinc-100 flex items-start gap-3">
                <div className="h-9 w-9 rounded-sm bg-zinc-100 text-zinc-600 flex items-center justify-center">
                  <Building2 className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Cliente</div>
                  <div className="text-sm font-medium text-zinc-900 flex items-center gap-2">
                    <span>{ticket.customer_name}</span>
                    {ticket.customer_language && (
                      <span
                        data-testid="customer-lang-badge"
                        title={`Lingua preferita: ${ticket.customer_language.toUpperCase()}`}
                        className="inline-flex items-center gap-1 rounded-sm border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] uppercase tracking-widest text-zinc-600"
                      >
                        <span className="text-sm leading-none">{({it:"🇮🇹",en:"🇬🇧",fr:"🇫🇷",es:"🇪🇸"})[ticket.customer_language] || "🇮🇹"}</span>
                        <span>{ticket.customer_language}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {(ticket.intervention_type || "hardware") === "software" && (
              <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="ticket-license-section">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-violet-800 font-semibold">Licenza WineIdea</div>
                  {ticket.license && (
                    <button
                      type="button"
                      onClick={() => updateField({ license_id: null })}
                      data-testid="ticket-license-unlink"
                      className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-red-700"
                    >
                      Cambia
                    </button>
                  )}
                </div>
                {ticket.license ? (
                  <div className="rounded-sm border border-violet-200 bg-violet-50 px-3 py-2 text-sm" data-testid="ticket-license-linked">
                    <div className="font-medium text-zinc-900 truncate">{ticket.license.negozio}</div>
                    <div className="text-[11px] text-zinc-600 mt-0.5">
                      {ticket.license.azienda ? `${ticket.license.azienda} · ` : ""}
                      <span className="font-mono text-violet-800">{ticket.license.codice_assistenza}</span>
                      {ticket.license.tipo_licenza ? ` · ${ticket.license.tipo_licenza}` : ""}
                    </div>
                    {ticket.license.rif_ordine && (
                      <div className="text-[10px] text-zinc-500 font-mono mt-0.5">Rif. ordine: {ticket.license.rif_ordine}</div>
                    )}
                  </div>
                ) : (
                  <>
                    <LicensePicker
                      value={null}
                      onChange={(lic) => { if (lic?.id) updateField({ license_id: lic.id }); }}
                      required
                    />
                    <div className="mt-2 text-[11px] text-red-700">
                      <AlertTriangle className="h-3 w-3 inline mr-1" />
                      Licenza mancante: aggancia la licenza per completare il ticket software.
                    </div>
                  </>
                )}
              </div>
            )}
            {ticket.whatsapp_from && (
              <div className="mt-4 pt-4 border-t border-zinc-100">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Rispondi via WhatsApp · {ticket.whatsapp_from}</div>
                <Textarea data-testid="wa-input" rows={3} placeholder={t("ticket_detail.wa_placeholder")} id="wa-msg" className="rounded-sm" />
                <Button data-testid="wa-send" size="sm" className="mt-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm w-full"
                  onClick={async () => {
                    const el = document.getElementById("wa-msg");
                    if (!el?.value?.trim()) return;
                    try { await api.post("/whatsapp/send", { ticket_id: id, text: el.value }); el.value = ""; toast.success(t("ticket_detail.toast_wa_sent")); await load(); }
                    catch (er) { toast.error(formatApiError(er)); }
                  }}>Invia WhatsApp</Button>
                {customerPricing && (
                  <div className="mt-3 pt-3 border-t border-zinc-100">
                    <SendPaymentLinkDialog
                      ticket={ticket}
                      pricing={customerPricing}
                      hasWhatsApp={!!ticket.whatsapp_from}
                      onSent={load}
                    />
                  </div>
                )}
              </div>
            )}
            {!ticket.whatsapp_from && (
              <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="wa-chat-block">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Contatto cliente</div>
                <div className="space-y-2">
                  <WhatsAppOpenChatButton ticket={ticket} className="w-full" onOpened={load} />
                  <EmailComposeButton ticket={ticket} attachments={attachments} className="w-full" onSent={load} />
                </div>
                <div className="mt-2 text-[11px] text-zinc-500">
                  Il messaggio di apertura Meta attiva la sessione: da lì potrai scrivere liberamente al cliente dalla piattaforma.
                </div>
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-zinc-100 text-xs text-zinc-500 space-y-1">
              <div>Creato da <span className="text-zinc-900">{ticket.created_by_name}</span></div>
              <div>{new Date(ticket.created_at).toLocaleString("it-IT")}</div>
              {ticket.reported_by && <div>Segnalato: {ticket.reported_by}</div>}
            </div>
          </Card>

          {ticket.dispenser?.model && (
            <Card className="rounded-sm border-zinc-200 bg-white p-6" data-testid="kb-files-side">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="h-4 w-4 text-brand" />
                <h3 className="font-display text-base font-semibold tracking-tight text-zinc-950">Manuali & Esplosi</h3>
              </div>
              <div className="text-xs text-zinc-500 mb-3">Documentazione tecnica per modello <span className="font-mono text-zinc-700">{ticket.dispenser.model}</span></div>
              <KnowledgeFilesPanel
                isAdmin={user?.role === "admin"}
                modelFilter={ticket.dispenser.model}
                compact
                limit={20}
              />
            </Card>
          )}

          <Card className="rounded-sm border-zinc-200 bg-white p-6" data-testid="quotes-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Receipt className="h-4 w-4 text-brand" />
                <div className="font-display font-semibold tracking-tight text-zinc-900">
                  Preventivi
                </div>
              </div>
              <Button
                size="sm"
                data-testid="new-quote-btn"
                onClick={() => setQuoteOpen(true)}
                className="h-8 bg-brand hover:bg-brand-dark text-white rounded-sm text-xs"
              >
                + Nuovo
              </Button>
            </div>
            <QuotesList quotes={quotes} onRefresh={load} />
          </Card>

          {(ticket.customer_id || ticket.dispenser?.customer_id) && (
            <CarnetPanel
              customerId={ticket.customer_id || ticket.dispenser?.customer_id}
              ticketId={id}
              userRole={user?.role}
              onUsage={load}
            />
          )}

          <TemplateSuggestions ticket={ticket} />

          <Card className="rounded-sm ai-panel-border p-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-brand" />
              <div className="font-display font-semibold tracking-tight text-zinc-900">
                Assistente AI · Gemini
              </div>
            </div>
            <p className="text-xs text-zinc-600 mb-3">
              Suggerimenti di diagnosi basati su ticket risolti in passato per problemi simili.
            </p>
            <Button
              data-testid="ai-suggest-btn"
              size="sm"
              onClick={runAi}
              disabled={ai.loading}
              className="w-full bg-brand hover:bg-brand-dark text-white rounded-sm"
            >
              {ai.loading ? (
                <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Analisi...</>
              ) : (
                <><Sparkles className="h-3.5 w-3.5 mr-2" /> Suggerisci soluzioni</>
              )}
            </Button>
            {ai.suggestion && (
              <div data-testid="ai-suggestion" className="mt-4 text-sm text-zinc-800 whitespace-pre-wrap bg-white border border-zinc-200 rounded-sm p-3 max-h-96 overflow-auto">
                {ai.suggestion}
              </div>
            )}
          </Card>
        </div>
      </div>

      <Dialog open={reconcileOpen} onOpenChange={setReconcileOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Aggiorna seriale dispenser</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-zinc-600">
              Seriale attuale: <span className="font-mono text-zinc-900">{ticket.dispenser_serial}</span>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Nuovo seriale dispenser *</Label>
              <Input
                data-testid="reconcile-serial-input"
                list="reconcile-serials"
                value={reconcileSerial}
                onChange={(e) => setReconcileSerial(e.target.value)}
                className="mt-1 rounded-sm font-mono"
                placeholder={t("ticket_detail.reconcile_serial_placeholder")}
                autoComplete="off"
                autoFocus
              />
              <datalist id="reconcile-serials">
                {dispensers.map((d) => (
                  <option key={d.id} value={d.serial_number}>
                    {d.model}{d.customer_name ? ` · ${d.customer_name}` : ""}
                  </option>
                ))}
              </datalist>
              <div className="mt-2 text-[11px] text-zinc-500 leading-relaxed">
                Se il seriale esiste in anagrafica il ticket sarà collegato al dispenser (garanzia, cliente, storico interventi).
                Altrimenti il ticket resterà marcato come &quot;da riconciliare&quot; ma il seriale digitato verrà salvato per riferimento futuro.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setReconcileOpen(false)}>Annulla</Button>
            <Button
              data-testid="reconcile-submit-btn"
              disabled={reconcileLoading}
              onClick={submitReconcile}
              className="bg-brand hover:bg-brand-dark text-white rounded-sm"
            >
              {reconcileLoading ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> {t("ticket_detail.reconcile_loading")}</> : t("ticket_detail.reconcile_action")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuoteDialog
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
        ticket={ticket}
        onCreated={load}
      />

      <CloseWithReportDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        ticket={ticket}
        onClosed={load}
      />
    </div>
  );
}

function TemplateSuggestions({ ticket }) {
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    const text = `${ticket?.title || ""} ${ticket?.description || ""}`.trim();
    if (text.length < 5) { setMatches([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/knowledge/match", { text });
        if (!cancelled) setMatches(data.matches || []);
      } catch (err) {
        // Non-fatal — the AI suggestions card just stays hidden if the endpoint fails
        console.debug("Template match request failed:", err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [ticket?.title, ticket?.description]);

  if (!matches.length) return null;

  return (
    <Card className="rounded-sm border-emerald-200 bg-emerald-50/40 p-5" data-testid="template-suggestions">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="h-4 w-4 text-emerald-700" />
        <div className="font-display font-semibold tracking-tight text-emerald-950 text-sm">
          Guide Base Conoscenza · {matches.length} suggerimenti
        </div>
      </div>
      <div className="space-y-2">
        {matches.map((m) => {
          const isOpen = open === m.id;
          return (
            <div key={m.id} className="border border-emerald-200 rounded-sm bg-white overflow-hidden">
              <button
                onClick={() => setOpen(isOpen ? null : m.id)}
                data-testid={`template-match-${m.id}`}
                className="w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-center justify-between gap-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-900 truncate">{m.title}</div>
                  <div className="text-[11px] text-emerald-800 mt-0.5">
                    Match: <b>{m.matched_terms?.join(", ")}</b>
                    {m.estimated_hours ? <> · ~{m.estimated_hours}h</> : null}
                    {m.severity ? <> · {m.severity}</> : null}
                  </div>
                </div>
                <ChevronLeft className={`h-4 w-4 text-zinc-400 transition-transform ${isOpen ? "-rotate-90" : "rotate-180"}`} />
              </button>
              {isOpen && (
                <div className="border-t border-emerald-100 px-3 py-2 bg-white text-xs text-zinc-700 space-y-1.5">
                  {m.symptom && <div><b>Sintomo:</b> {m.symptom}</div>}
                  {m.resolution && <div><b>Risoluzione:</b> {m.resolution}</div>}
                  {m.part_codes?.length > 0 && <div><b>Ricambi:</b> {m.part_codes.join(", ")}</div>}
                  <Link to={`/conoscenza`} className="inline-block text-brand hover:underline text-[11px] mt-1">Vedi in Base Conoscenza →</Link>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

